/**
 * Thin logger that mutes `debug`/`info` in production. Avoid dumping logs
 * from libraries into end-user consoles.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

function resolveMinLevel(): LogLevel {
  // Vite sets import.meta.env.PROD / DEV; fall back to Node env.
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
  const mode = env?.NODE_ENV ?? 'development'
  return mode === 'production' ? 'warn' : 'debug'
}

export interface Logger {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

export function createLogger(scope: string): Logger {
  const min = LEVEL_ORDER[resolveMinLevel()]
  const tag = `[${scope}]`
  const enabled = (lvl: LogLevel) => LEVEL_ORDER[lvl] >= min
  return {
    debug: (...args) => enabled('debug') && console.debug(tag, ...args),
    info: (...args) => enabled('info') && console.info(tag, ...args),
    warn: (...args) => enabled('warn') && console.warn(tag, ...args),
    error: (...args) => enabled('error') && console.error(tag, ...args),
  }
}
